// ApiIntegrations.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { cleanString } from '../apiUtils';
import { resolveTypeRows } from './utils/integrationUtils';

// Hooks
import useIncomingIntegrations from './hooks/useIncomingIntegrations';
import useOutboundIntegrations from './hooks/useOutboundIntegrations';

// CSS
import './ApiIntegrations.css';

// Components
import IncomingHooksSection from './components/IncomingHooksSection';
import IntegrationDeleteConfirms from './components/IntegrationDeleteConfirms';
import IncomingCreateWebhookModal from './components/modals/IncomingCreateWebhookModal';
import IncomingWebhookCredentialsModal from './components/modals/IncomingWebhookCredentialsModal';
import IncomingWebhookSetupModal from './components/modals/IncomingWebhookSetupModal';
import OutboundCreateRuleModal from './components/modals/OutboundCreateRuleModal';
import OutboundEditRuleModal from './components/modals/OutboundEditRuleModal';
import OutboundRulesSection from './components/OutboundRulesSection';
import Divider from 'components/divider/Divider';
import { toast } from 'react-toastify';

/**
 * ApiIntegrations
 *
 * Renders and manages incoming webhook and outbound rule integrations for a base.
 *
 * @param {string} baseId - The active base identifier.
 * @param {string} baseRole - The current user's role in the active base.
 * @param {array} schemaTypes - The schema types available to the base.
 *
 * @returns {ReactNode} The rendered integrations management page.
 */
const ApiIntegrations = ({ baseId, baseRole, schemaTypes }) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Error Message State

    const [errorMessage, setErrorMessage] = useState('');

    // Status Message State
    const [statusMessage, setStatusMessage] = useState('');

    // Busy Action Key State
    const [busyActionKey, setBusyActionKey] = useState('');

    // ----- MEMOS ----- //

    // Memoized normalized schema type rows.

    const typeRows = useMemo(() => resolveTypeRows(schemaTypes), [schemaTypes]);

    // Memoized type label map by lowercased type id.
    const typeLabelMap = useMemo(() => {
        const map = new Map();
        typeRows.forEach((row) => {
            map.set(cleanString(row.typeId).toLowerCase(), row.label);
        });
        return map;
    }, [typeRows]);

    const incomingSecretHeaderName = 'X-Appdeck-Incoming-Secret';
    const canManageCredentials = baseRole === 'owner' || baseRole === 'admin';

    // ----- HOOKS ----- //

    // Incoming integrations state and handlers.

    const incoming = useIncomingIntegrations({
        baseId,
        canManageCredentials,
        typeRows,
        incomingSecretHeaderName,
        setErrorMessage,
        setStatusMessage,
        setBusyActionKey,
    });

    // Outbound integrations state and handlers.
    const outbound = useOutboundIntegrations({
        baseId,
        canManageCredentials,
        typeRows,
        setErrorMessage,
        setStatusMessage,
        setBusyActionKey,
    });

    // ----- MEMOS ----- //

    /**
     * Copies text to the clipboard and shows a success toast when possible.
     *
     * @param {string} value - The source value to copy.
     * @param {string} successMessage - Optional success toast message.
     *
     * @returns {Promise<void>} Resolves when copy logic completes.
     */

    const copyToClipboard = useCallback(async (value, successMessage) => {
        const text = cleanString(value);
        if (!text) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                toast.success(successMessage || 'Copied.');
                return;
            }
        } catch (_error) {
            // fall through to prompt fallback
        }

        window.prompt('Copy this value', text);
    }, []);
    if (!baseId) {
        return (
            <div
              className="api-integrations-note"
            >
                Select an active base to manage integrations.
            </div>
        );
    }

    // ----- RENDER ----- //

    return (
        <div className="api-integrations-container">
            {!canManageCredentials && (
                <div
                  className="api-integrations-note"
                >
                    Read-only mode for this user. Owners and admins can manage integration rules.
                </div>
            )}

            {/* ERROR */}
            {errorMessage && (

                <div className="api-integrations-error">{errorMessage}</div>
            )}

            {/* STATUS */}
            {statusMessage && !errorMessage && (

                <div className="api-integrations-status">{statusMessage}</div>
            )}

            <IncomingHooksSection
              theme={theme}
              canManageCredentials={canManageCredentials}
              incomingLoading={incoming.incomingLoading}
              sortedIncomingHooks={incoming.sortedIncomingHooks}
              typeLabelMap={typeLabelMap}
              busyActionKey={busyActionKey}
              onAddWebhook={incoming.handleOpenIncomingCreateModal}
              onCopyUrl={copyToClipboard}
              onDeletePrompt={incoming.handleIncomingDeletePrompt}
              onOpenSetup={incoming.handleOpenIncomingSetupModal}
              onToggleStatus={incoming.handleToggleIncomingHookStatus}
            />

            <Divider />

            <OutboundRulesSection
              theme={theme}
              canManageCredentials={canManageCredentials}
              loading={outbound.loading}
              sortedRules={outbound.sortedRules}
              typeLabelMap={typeLabelMap}
              busyActionKey={busyActionKey}
              onAddRule={outbound.handleOpenCreateModal}
              onDeleteRule={outbound.handleDeletePrompt}
              onEditRule={outbound.handleOpenEditModal}
              onTestRule={outbound.handleTestRule}
              onToggleRuleStatus={outbound.handleToggleRuleStatus}
            />

            <IncomingCreateWebhookModal
              isOpen={incoming.incomingCreateModalOpen}
              canManageCredentials={canManageCredentials}
              busyActionKey={busyActionKey}
              incomingDraftName={incoming.incomingDraftName}
              incomingDraftAction={incoming.incomingDraftAction}
              incomingDraftTypeId={incoming.incomingDraftTypeId}
              typeRows={typeRows}
              onClose={incoming.handleIncomingCreateModalClose}
              onCreate={incoming.handleCreateIncomingHook}
              onDraftActionChange={incoming.setIncomingDraftAction}
              onDraftNameChange={incoming.setIncomingDraftName}
              onDraftTypeIdChange={incoming.setIncomingDraftTypeId}
            />

            <IncomingWebhookCredentialsModal
              theme={theme}
              isOpen={incoming.incomingRevealModalOpen}
              createdIncomingHookUrl={incoming.createdIncomingHookUrl}
              createdIncomingHookSecret={incoming.createdIncomingHookSecret}
              createdIncomingHeaderPreview={incoming.createdIncomingHeaderPreview}
              createdIncomingFieldRows={incoming.createdIncomingFieldRows}
              incomingSecretHeaderName={incomingSecretHeaderName}
              onClose={incoming.handleIncomingRevealModalClose}
              onCopyToClipboard={copyToClipboard}
            />

            <IncomingWebhookSetupModal
              theme={theme}
              isOpen={incoming.incomingSetupModalOpen}
              selectedIncomingHookUrl={incoming.selectedIncomingHookUrl}
              selectedIncomingHeaderPreview={incoming.selectedIncomingHeaderPreview}
              selectedIncomingFieldRows={incoming.selectedIncomingFieldRows}
              incomingSecretHeaderName={incomingSecretHeaderName}
              onClose={incoming.handleIncomingSetupModalClose}
              onCopyToClipboard={copyToClipboard}
            />

            <OutboundCreateRuleModal
              isOpen={outbound.createModalOpen}
              canManageCredentials={canManageCredentials}
              busyActionKey={busyActionKey}
              draftName={outbound.draftName}
              draftEvent={outbound.draftEvent}
              draftTypeId={outbound.draftTypeId}
              draftDestinationUrl={outbound.draftDestinationUrl}
              draftDestinationName={outbound.draftDestinationName}
              typeRows={typeRows}
              onClose={outbound.handleCreateModalClose}
              onCreateRule={outbound.handleCreateRule}
              onDraftDestinationNameChange={outbound.setDraftDestinationName}
              onDraftDestinationUrlChange={outbound.setDraftDestinationUrl}
              onDraftEventChange={outbound.setDraftEvent}
              onDraftNameChange={outbound.setDraftName}
              onDraftTypeIdChange={outbound.setDraftTypeId}
            />

            <OutboundEditRuleModal
              theme={theme}
              isOpen={outbound.editModalOpen}
              canManageCredentials={canManageCredentials}
              busyActionKey={busyActionKey}
              editingRule={outbound.editingRule}
              editDraftName={outbound.editDraftName}
              editDraftEvent={outbound.editDraftEvent}
              editDraftTypeId={outbound.editDraftTypeId}
              editDraftStatus={outbound.editDraftStatus}
              editDraftDestinationUrl={outbound.editDraftDestinationUrl}
              editDraftDestinationName={outbound.editDraftDestinationName}
              typeRows={typeRows}
              onClose={outbound.handleEditModalClose}
              onEditDraftDestinationNameChange={outbound.setEditDraftDestinationName}
              onEditDraftDestinationUrlChange={outbound.setEditDraftDestinationUrl}
              onEditDraftEventChange={outbound.setEditDraftEvent}
              onEditDraftNameChange={outbound.setEditDraftName}
              onEditDraftStatusChange={outbound.setEditDraftStatus}
              onEditDraftTypeIdChange={outbound.setEditDraftTypeId}
              onSave={outbound.handleSaveEditRule}
            />

            <IntegrationDeleteConfirms
              deleteConfirmMessage={outbound.deleteConfirmMessage}
              deleteConfirmOpen={outbound.deleteConfirmOpen}
              incomingDeleteConfirmMessage={incoming.incomingDeleteConfirmMessage}
              incomingDeleteConfirmOpen={incoming.incomingDeleteConfirmOpen}
              onDeleteConfirmOpen={outbound.handleDeleteConfirmOpen}
              onDeleteIncomingHook={incoming.handleDeleteIncomingHook}
              onDeleteRule={outbound.handleDeleteRule}
              onIncomingDeleteConfirmOpen={incoming.handleIncomingDeleteConfirmOpen}
            />
        </div>
    );
};

// Dev Tools
ApiIntegrations.displayName = 'ApiIntegrations';

export default ApiIntegrations;
